<template>
  <fieldset
    class="agila-radio-images"
    role="group"
    :aria-label="props.label ? props.label : props.ariaLabel"
  >
    <label :for="props.id" v-if="props.label && props.showLabel">{{
      props.label
    }}</label>
    <agila-form-error
      :error="error"
      :id="props.id + '-error'"
    ></agila-form-error>
    <div class="radio-image-wrapper" :class="{ image: props.options[0].image }">
      <div
        tabindex="0"
        class="radio-image-con"
        :class="{
          active: inputValue.id === option.id,
          [props.class]: true,
          'input-error': error,
          blue: props.blue,
        }"
        v-for="option of props.options"
        :key="option.id"
        @keyup.enter="
          inputValue = option;
          readyForCheck = true;
          updateValue();
        "
        @click="
          inputValue = option;
          readyForCheck = true;
          updateValue();
        "
        :data-cy="(props.cy ? props.cy + '_' : '') + props.id + '_' + option.id"
      >
        <div class="radio-image-content" :class="{ 'd-flex': !option.image }">
          <RadioButton
            v-model="inputValue"
            :inputId="option.id.toString()"
            :name="props.id"
            :value="option"
            :tabindex="-1"
            :aria-describedby="props.id + '-error'"
            :aria-labelledby="'label_' + option.name"
          />
          <div v-if="option.image" :class="option.image"></div>
          <label :for="option.id" :id="'label_' + option.name">{{
            option.name
          }}</label>
        </div>
      </div>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { defineEmits, defineProps, withDefaults, defineExpose } from "vue";
import RadioButton from "primevue/radiobutton";
import useSharedFormLogic from "../helper/functions/useSharedFormLogic";
import AgilaFormError from "./agilaFormError.vue";

interface Props {
  modelValue: any;
  id: string;
  options: Array<any>;
  label?: string;
  rules?: string[];
  class?: string;
  blue?: boolean;
  cy?: string;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  id: "",
  options: () => [],
  label: "",
  rules: () => [],
  class: "",
  blue: false,
  cy: "",
  ariaLabel: "",
});

const emit = defineEmits<{
  (e: "update:modelValue", modelValue: any): void;
  (e: "update:error", error: string): void;
}>();

const { readyForCheck, inputValue, error, updateValue, validate } =
  useSharedFormLogic(props, emit);

defineExpose({
  validate: () => {
    readyForCheck.value = true;
    return validate(inputValue.value);
  },
});
</script>

<style scoped lang="scss">
.agila-radio-images {
  margin-bottom: 20px;

  .radio-image-wrapper {
    margin-top: 8px;

    .radio-image-con {
      display: flex;
      cursor: pointer;
      background-color: var(--color-blue-100);
      border-radius: 8px;
      width: 100%;
      height: 54px;
      margin-bottom: 16px;
      box-sizing: border-box;
      box-shadow: 2px 2px 4px -1px rgba(0, 0, 0, 0.25);

      &.bg-white {
        background-color: var(--color-white);
      }

      &:hover,
      &.active {
        background: var(--color-blue-200) !important;
      }

      &:focus-visible {
        box-shadow: 0 0 0 2px var(--color-yellow-300),
          0 0 0 4px var(--color-blue-focus-line) !important;
        color: var(--color-gray-900);
        border-color: var(--color-yellow-300);
        outline: none;

        &:hover {
          border-color: var(--color-yellow-300);
          color: var(--color-gray-900);
        }
      }

      .radio-image-content {
        margin: auto;
        text-align: center;
        display: flex;
        flex-flow: column;
      }

      label {
        cursor: pointer;
        color: var(--color-gray-900);
      }

      &.input-error {
        border: 2px solid var(--color-red-error) !important;
      }

      &.blue {
        background-color: var(--color-blue-standard);
        color: var(--color-white);

        &:hover {
          background-color: var(--color-blue-600) !important;
        }
      }
    }

    &.image {
      display: flex;

      .radio-image-con {
        height: 100px;
        margin-left: 16px;
        margin-bottom: 0;

        &:nth-of-type(1) {
          margin-left: 0;
        }

        label {
          display: block;
        }
      }
    }
  }

  .p-radiobutton {
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;

    .p-radiobutton-box {
      height: 0;
    }
  }
}
</style>
